import moment from 'moment'
import produce from 'immer'
import Actions from '../actions'
import LogRocket from 'logrocket'
import { getProfilePicture } from '../utils/profilePictureUtil'
import { serializeMySubscriptions } from '../utils/serializerUtil'
import { capitalize } from 'lodash'
import { COMPLETED_HEALTH_HISTORY } from '../consts/OnBoardingConsts'
import { ProviderPCPTypes } from '../consts/ProviderConsts'

const initialState = {
  userDetails: null,
  owner: null,
  personalDoctor: null,
  isUserDetailsLoading: false,
  isFetchingUserDetailsSuccess: false,
  isEmergencyUpdateForMember: false,
  isGenderDetailsLoading: false,
  isContactInformationLoading: false,
  isPhoneNumbersVerificationLoading: false,
  phoneNumberToVerify: null,
  isPhoneNuberToVerifyChecked: false,
  isPhoneNumbersVerifySuccess: false,
  updateContactInformationError: null,
  isContactInformationUpdateSuccess: false,
  isSecretQuestionUpdateLoading: false,
  secretQuestionsError: null,
  updateLanguageError: null,
  isLanguageUpdateLoading: false,
  isCommunicationPreferencesLoading: false,
  communicationPreferencesError: null,
  isCommunicationPreferencesConfigureMode: false,
  authenticatedLinks: null,
  membersAccumulators: null,
  membersAccumulatorsError: null,
  isMembersAccumulatorsLoading: false,
  patientsPcpProviders: []
}

const getAge = dateOfBirth => {
  if (dateOfBirth) {
    return moment().diff(dateOfBirth, 'years')
  } else {
    return null
  }
}

export default produce((draft, action) => {
  const { payload } = action

  switch (action.type) {
    case Actions.USER_PROFILE_REQUESTED: {
      draft.isUserDetailsLoading = true
      break
    }
    case Actions.PROFILE_PICTURE_RECEIVED: {
      const profilePicture = payload

      if (profilePicture) {
        draft.userDetails.profilePicture = getProfilePicture(profilePicture)
      }
      break
    }
    case Actions.USER_PROFILE_PICTURE_CHANGE_REQUESTED: {
      draft.userDetails.profilePicture = getProfilePicture(
        payload?.profilePicture
      )
      break
    }
    case Actions.USER_PROFILE_RECEIVED: {
      const {
        profilePicture,
        dateOfBirth,
        idUploaded,
        emergency_contact,
        default_pharmacy,
        referrer,
        account,
        payments, // deconstract but dont use it! use state.account instead
        ...profile
      } = {
        ...payload?.profile
      }

      const emergencyContact = {
        phoneNumber: emergency_contact?.phone_number,
        contactName: emergency_contact?.contact_name
      }

      draft.userDetails = {
        ...profile,
        dateOfBirth,
        firstName: capitalize(profile?.firstName || ''),
        middleName: capitalize(profile?.middleName || ''),
        lastName: capitalize(profile?.lastName || ''),
        payments: {
          ...payments,
          my_subscriptions: serializeMySubscriptions(payments?.my_subscriptions)
        }
      }
      draft.userDetails.age = getAge(dateOfBirth)
      draft.userDetails.profilePicture = getProfilePicture(profilePicture)
      draft.userDetails.idUploaded = idUploaded
      draft.userDetails.emergencyContact = emergencyContact
      draft.userDetails.pharmacy = default_pharmacy
      draft.userDetails.referrer = referrer
      draft.userDetails.isOwner = account?.owner === profile?.id

      LogRocket.identify(draft.userDetails.id?.toString(), {
        email: draft.userDetails.email,
        name: `${draft.userDetails.firstName} ${draft.userDetails.lastName}`,
        hmo_member_id: draft.userDetails.hmo_member_id,
        is_test: draft.userDetails.is_test
      })
      if (draft.userDetails?.hmo_member_id) {
        LogRocket.track('HMO_MEMBER_LOGIN')
      }

      draft.personalDoctor = account?.personalDoctor
      draft.owner = account?.owner
      draft.isUserDetailsLoading = false
      draft.isFetchingUserDetailsSuccess = true
      break
    }
    case Actions.SIGNOUT_RECEIVED: {
      return initialState
    }
    case Actions.UPDATE_EMERGENCY_CONTACT_PRESSED: {
      const { isEmergencyUpdateForMember = false } = payload || {}
      draft.isEmergencyUpdateForMember = isEmergencyUpdateForMember
      break
    }
    case Actions.OPERATION_FAILED: {
      draft.isUserDetailsLoading = false
      break
    }
    case Actions.UPDATE_SEX_REQUESTED: {
      const { sex } = payload
      draft.userDetails.sex = sex
      break
    }
    case Actions.UPDATE_EMERGENCY_CONTACT_RECEIVED: {
      const { contactName, phoneNumber, patientId } = payload
      if (patientId === draft.userDetails.id) {
        draft.userDetails.emergencyContact = {
          phoneNumber,
          contactName
        }
      }
      break
    }
    case Actions.UPDATE_MY_PHARMACY_RECEIVED: {
      const { pharmacy, patientId } = payload
      if (patientId === draft.userDetails.id) {
        draft.userDetails.pharmacy = pharmacy
      }
      break
    }
    case Actions.UPDATE_GENDER_DETAILS_REQUESTED: {
      draft.isGenderDetailsLoading = true
      break
    }
    case Actions.UPDATE_GENDER_DETAILS_RECEIVED: {
      const { genderIdentity, pronoun } = payload

      draft.userDetails.genderIdentity = genderIdentity
      draft.userDetails.pronoun = pronoun
      draft.isGenderDetailsLoading = false
      break
    }
    case Actions.CONTACT_INFORMATION_PRESSED: {
      draft.isPhoneNuberToVerifyChecked = false
      break
    }
    case Actions.UPDATE_CONTACT_INFORMATION_REQUESTED: {
      draft.isContactInformationLoading = true
      draft.isContactInformationUpdateSuccess = false
      draft.updateContactInformationError = null
      break
    }
    case Actions.UPDATE_CONTACT_INFORMATION_RECEIVED: {
      const {
        phone,
        email,
        mailingAddress,
        mailingAddress2,
        mailingCity,
        mailingState,
        mailingZipCode
      } = payload
      draft.isContactInformationLoading = false
      draft.userDetails.phoneNumber = phone
      draft.userDetails.email = email
      draft.userDetails.address = draft.userDetails.address.map(address => {
        if (address.address_type === 'Mailing') {
          address.address1 = mailingAddress
          address.address2 = mailingAddress2
          address.city = mailingCity
          address.state = mailingState
          address.zipCode = mailingZipCode
        }
        return address
      })
      draft.isPhoneNumbersVerifySuccess = false
      draft.isContactInformationUpdateSuccess = true
      break
    }
    case Actions.UPDATE_CONTACT_INFORMATION_FAILED: {
      const { error } = payload
      draft.isContactInformationLoading = false
      draft.updateContactInformationError = error.message
      break
    }
    case Actions.UPDATE_CONTACT_INFORMATION_RESET_ERROR: {
      draft.updateContactInformationError = null
      break
    }
    case Actions.PHONE_NUMBER_VERIFICATION_REQUESTED: {
      draft.phoneNumberToVerify = payload.phoneNumber
      draft.updateContactInformationError = null
      draft.isPhoneNumbersVerifySuccess = false
      draft.isContactInformationLoading = true
      draft.isPhoneNuberToVerifyChecked = false
      break
    }
    case Actions.PHONE_NUMBER_VERIFICATION_FAILED: {
      const { error } = payload
      draft.isContactInformationLoading = false
      draft.updateContactInformationError = error.message
      draft.isPhoneNuberToVerifyChecked = false
      break
    }
    case Actions.PHONE_NUMBER_VERIFICATION_RECEIVED: {
      draft.isContactInformationLoading = false
      draft.isPhoneNuberToVerifyChecked = true
      break
    }
    case Actions.PHONE_NUMBER_CODE_VERIFY_REQUESTED: {
      draft.isPhoneNumbersVerificationLoading = true
      break
    }
    case Actions.PHONE_NUMBER_CODE_VERIFY_RECEIVED: {
      draft.isPhoneNumbersVerificationLoading = false
      draft.isPhoneNumbersVerifySuccess = true
      break
    }
    case Actions.SECRET_QUESTION_UPDATE_REQUESTED: {
      draft.isSecretQuestionUpdateLoading = true
      break
    }
    case Actions.SECRET_QUESTION_UPDATE_RECEIVED: {
      draft.isSecretQuestionUpdateLoading = false
      draft.userDetails.isSecretQuestions = true
      break
    }
    case Actions.SECRET_QUESTION_UPDATE_FAILED: {
      const { error } = payload
      draft.secretQuestionsError = error.message
      draft.isSecretQuestionUpdateLoading = false
      break
    }
    case Actions.UPDATE_LANGUAGE_REQUESTED: {
      draft.isLanguageUpdateLoading = true
      break
    }
    case Actions.UPDATE_LANGUAGE_RECEIVED: {
      const { language } = payload
      draft.userDetails.language = language
      draft.isLanguageUpdateLoading = false
      break
    }
    case Actions.UPDATE_LANGUAGE_FAILED: {
      const { error } = payload
      draft.updateLanguageError = error.message
      draft.isLanguageUpdateLoading = false
      break
    }
    case Actions.FRONTEND_AUTHENTICATED_URLS_RECEIVED: {
      draft.authenticatedLinks = { ...payload }
      break
    }
    case Actions.MEMBERS_ACCUMULATORS_REQUESTED: {
      draft.isMembersAccumulatorsLoading = true
      break
    }
    case Actions.MEMBERS_ACCUMULATORS_RECEIVED: {
      draft.membersAccumulators = payload
      draft.isMembersAccumulatorsLoading = false
      break
    }
    case Actions.MEMBERS_ACCUMULATORS_FAILED: {
      const { error } = payload
      draft.isMembersAccumulatorsLoading = false
      draft.membersAccumulatorsError = error.body?.[0]
      break
    }
    case Actions.UPDATE_COMMUNICATION_PREFERENCES_REQUESTED: {
      draft.isCommunicationPreferencesUpdateLoading = true
      break
    }
    case Actions.UPDATE_COMMUNICATION_PREFERENCES_RECEIVED: {
      draft.userDetails.communicationPreferences = payload.preferences
      draft.isCommunicationPreferencesUpdateLoading = false
      draft.userDetails.isCommunicationPreferences = true
      break
    }
    case Actions.UPDATE_COMMUNICATION_PREFERENCES_FAILED: {
      const { error } = payload
      draft.updateCommunicationPreferencesError = error.message
      break
    }
    case Actions.RISK_ASSESMENT_BOT_ENDED: {
      draft.userDetails.hmo_data.on_boarding_tasks = [
        ...draft.userDetails.hmo_data.on_boarding_tasks,
        {
          task: COMPLETED_HEALTH_HISTORY.name,
          is_completed: true,
          completed_at: new Date()
        }
      ]
      break
    }
    case Actions.PROVIDER_PCP_RECEIVED: {
      // in case the pcp is updated for the current user, add this data to profile
      // this should be here in order to present the user pcp on the profile screen
      // without depending to the family selected member
      if (payload?.patientId === draft.userDetails?.id) {
        const pcpProviders = payload?.pcpProviders
        const telehealthPcp = pcpProviders?.find(
          provider => !!provider?.telehealth
        )
        const physicalPcp = pcpProviders?.filter(
          provider => !provider?.telehealth
        )
        draft.userDetails.telehealthPcp = telehealthPcp
        draft.userDetails.physicalPcp = physicalPcp
      }

      const pcpByType = payload?.pcpByType

      pcpByType.forEach(pcp => {
        draft.patientsPcpProviders.push({
          patientId: pcp.patient,
          pcpId: pcp.provider_accepting_address,
          type:
            pcp.pcp_type === ProviderPCPTypes.TELEHEALTH
              ? ProviderPCPTypes.TELEHEALTH
              : ProviderPCPTypes.PHYSICAL
        })
      })

      break
    }
    case Actions.OPEN_COMMUNICATION_PREFERENCES_CONFIGURE: {
      draft.isCommunicationPreferencesConfigureMode = true
      break
    }
    default: {
      // Nothing to do
      break
    }
  }
}, initialState)
